export type Session = {
  access_token: string;
  user: { id: string; telegram_id: number; first_name: string; username?: string; role: "user" | "owner" };
};

export type Style = { id: string; slug: string; name: string; description: string; version: number };
export type Job = { id: string; status: string; result_asset_id?: string | null; error_code?: string | null };
export type GenerationAccess = { remaining: number | null; unlimited: boolean; attempts_used: number; attempt_limit: number | null };
export type ProfileSnapshot = {
  membership: "member" | "not_member" | "unknown";
  attempts_used: number;
  completed_works: number;
  opened_materials: string[];
  completed_materials: string[];
  material_progress: Record<string, number>;
  purchased_courses: string[];
};
export type ContentItem = { id: string; type: "article" | "course" | "prompt"; slug: string; title: string; summary: string; body: string; cover_asset_id?: string | null; published_at: string };
export type MaterialEvent = "open" | "complete" | "copy" | "video_start" | "video_progress" | "video_complete";
export type AssetLinks = { preview_url: string; download_url: string; file_name: string; expires_at: number };
export type AdminOverview = {
  metrics: { users: number; users_24h: number; generations: number; succeeded: number; active: number; reconciliation: number; materials_opened: number; materials_completed: number; engaged_users: number; course_purchases: number; estimated_cost_micro_usd: number; average_cost_micro_usd: number; last_cost_micro_usd: number };
  settings: { generation_enabled: boolean; daily_budget_micro_usd: number | null; monthly_budget_micro_usd: number | null; queue_limit: number };
};
export type AdminUserMaterial = {
  id: string; title: string; type?: "article" | "course" | "prompt" | null;
  progress: number; completed: boolean; copied: boolean; last_event: MaterialEvent;
  last_opened_at: string;
};
export type AdminUser = {
  id: string; telegram_id: number; first_name: string; username?: string | null;
  role: "user" | "owner"; created_at: string; last_seen_at: string; photo_url?: string | null;
  credits: number; membership_state?: "member" | "not_member" | "unknown" | null;
  membership_raw_status?: string | null; membership_checked_at?: string | null;
  generation_attempts: number; avatars_created: number; last_generation_at?: string | null;
  materials_opened: number; materials_completed: number; prompts_copied: number;
  videos_started: number; videos_completed: number; last_learning_at?: string | null;
  course_accesses: number; course_purchases: number; recent_materials: AdminUserMaterial[];
};
export type FeatureAvailability = { enabled: boolean; audience?: "owner" | "all" | null; version: number };
export type FeatureRollout = { feature_key: string; audience: "owner" | "all"; version: number; updated_at: string };
export type ProductIdea = {
  title: string; what_it_is: string; audience: string; problem: string;
  buyer_gets: string; how_to_make: string; build_time: string; price: string;
  social_content: [string, string];
};
export type ProductIdeasResult = { valid: true; error: null; cards: ProductIdea[] };
export type ProductIdeasJob = {
  id: string; status: string; result?: ProductIdeasResult | null; error_code?: string | null;
  created_at?: string; finished_at?: string | null;
};
export type ProductIdeasAccess = { attempts_used: number; attempt_limit: 3; remaining: number };

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

async function request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch {
    throw new Error("NETWORK_ERROR");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const fallback = response.status === 413 ? "FILE_TOO_LARGE" : `HTTP_${response.status}`;
    throw new Error(body.detail ?? fallback);
  }
  return response.json() as Promise<T>;
}

async function requestBlob(path: string, token: string): Promise<Blob> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`HTTP_${response.status}`);
  return response.blob();
}

export const api = {
  login(initData: string) {
    return request<Session>("/api/auth/telegram", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ init_data: initData }),
    });
  },
  membership(token: string) {
    return request<{ state: "member" | "not_member" | "unknown" }>("/api/membership", {}, token);
  },
  profileAvatar(token: string) {
    return requestBlob("/api/me/avatar", token);
  },
  profileSnapshot(token: string) {
    return request<ProfileSnapshot>("/api/profile-snapshot", {}, token);
  },
  features(token: string) {
    return request<{ items: Record<string, FeatureAvailability> }>("/api/features", {}, token);
  },
  materialEvent(id: string, event: MaterialEvent, progressPercent: number, token: string) {
    return request<{ recorded: boolean }>(`/api/activity/materials/${encodeURIComponent(id)}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event, progress_percent: progressPercent }),
    }, token);
  },
  content(token: string) {
    return request<{ items: ContentItem[] }>("/api/content", {}, token);
  },
  coursePreview(slug: string, token: string) {
    return request<{ playback_url: string; expires_at: number; watermark: string }>(`/api/courses/${encodeURIComponent(slug)}/preview`, { method: "POST" }, token);
  },
  styles() {
    return request<{ items: Style[] }>("/api/styles");
  },
  generationAccess(styleId: string, token: string) {
    return request<GenerationAccess>(`/api/generation-access?style_id=${encodeURIComponent(styleId)}`, {}, token);
  },
  upload(file: File, token: string) {
    const body = new FormData();
    body.set("file", file);
    return request<{ id: string }>("/api/assets?purpose=avatar_source", { method: "POST", body }, token);
  },
  generate(sourceAssetId: string, styleId: string, token: string) {
    return request<{ job: Job }>("/api/generations", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ source_asset_id: sourceAssetId, style_id: styleId }),
    }, token);
  },
  productIdeasAccess(token: string) {
    return request<ProductIdeasAccess>("/api/product-ideas/access", {}, token);
  },
  productIdeasJobs(token: string) {
    return request<{ items: ProductIdeasJob[] }>("/api/product-ideas/jobs", {}, token);
  },
  createProductIdeas(text: string, requestKey: string, token: string) {
    return request<{ job: ProductIdeasJob }>("/api/product-ideas/jobs", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": requestKey },
      body: JSON.stringify({ text }),
    }, token);
  },
  productIdeasJob(id: string, token: string) {
    return request<{ job: ProductIdeasJob }>(`/api/product-ideas/jobs/${encodeURIComponent(id)}`, {}, token);
  },
  job(id: string, token: string) {
    return request<{ job: Job }>(`/api/generations/${id}`, {}, token);
  },
  generations(styleId: string, token: string) {
    return request<{ items: Job[] }>(`/api/generations?style_id=${encodeURIComponent(styleId)}`, {}, token);
  },
  allGenerations(token: string) {
    return request<{ items: Job[] }>("/api/generations", {}, token);
  },
  assetLinks(id: string, token: string) {
    return request<AssetLinks>(`/api/assets/${id}/links`, { method: "POST" }, token);
  },
  adminOverview(token: string) {
    return request<AdminOverview>("/api/admin/overview", {}, token);
  },
  adminUsers(token: string, page = 1, pageSize = 5) {
    return request<{ items: AdminUser[]; page: number; page_size: number; total: number; pages: number }>(`/api/admin/users?page=${page}&page_size=${pageSize}`, {}, token);
  },
  refreshAdminUserMembership(id: string, token: string) {
    return request<{ membership: { state: AdminUser["membership_state"]; raw_status?: string | null; checked_at: string } }>(`/api/admin/users/${encodeURIComponent(id)}/refresh-membership`, { method: "POST" }, token);
  },
  updateAdminSettings(settings: AdminOverview["settings"], token: string) {
    return request<{ settings: AdminOverview["settings"] }>("/api/admin/settings", {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(settings),
    }, token);
  },
  adminFeatures(token: string) {
    return request<{ items: FeatureRollout[] }>("/api/admin/features", {}, token);
  },
  updateAdminFeature(feature: FeatureRollout, audience: FeatureRollout["audience"], token: string) {
    return request<{ feature: FeatureRollout }>(`/api/admin/features/${encodeURIComponent(feature.feature_key)}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ audience, expected_version: feature.version }),
    }, token);
  },
};
